(() => {
  const registry = Symbol.for('tl.header.search.initialize');
  if (window[registry]) { window[registry](document); return; }
  const instances = new WeakMap();
  class HeaderSearch {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('input[type=search]');
      this.form = this.input.form;
      this.shell = root.querySelector('[data-search-shell]');
      this.defaults = root.querySelector('[data-search-default]');
      this.results = root.querySelector('[data-search-results]');
      this.error = root.querySelector('[data-search-error]');
      this.reset = root.querySelector('[type=reset]');
      this.status = root.querySelector('[data-search-status]');
      this.cache = new Map();
      this.sequence = 0;
      this.selected = -1;
      this.events = new AbortController();
      const signal = this.events.signal;
      // Header sections can be replaced independently of this persistent search section.
      document.addEventListener('click', event => {
        const toggle = event.target.closest('[data-tl-search-toggle]');
        if (!toggle || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.root.hidden ? this.open(toggle) : this.close(true);
      }, { signal });
      document.addEventListener('keydown', event => {
        const toggle = event.target.closest('[data-tl-search-toggle]');
        if (toggle && event.key === ' ') { event.preventDefault(); toggle.click(); }
      }, { signal });
      this.input.addEventListener('input', () => this.schedule(), { signal });
      this.form.addEventListener('reset', () => {
        this.input.value = '';
        this.clear();
        this.input.focus();
      }, { signal });
      this.form.addEventListener('submit', event => {
        if (!this.input.value.trim()) event.preventDefault();
      }, { signal });
      root.addEventListener('click', event => {
        if (event.target === root) this.close(false);
        const keyword = event.target.closest('[data-search-keyword]');
        if (keyword) {
          this.input.value = keyword.dataset.searchKeyword;
          this.input.focus();
          this.schedule(true);
        }
      }, { signal });
      root.addEventListener('keydown', event => this.keydown(event), { signal });
      document.addEventListener('tl:header:open-drawer', () => this.close(false), { signal });
      document.addEventListener('pointerdown', event => {
        if (!root.hidden && !root.contains(event.target) && !this.toggles.some(t => t.contains(event.target))) this.close(false);
      }, { signal });
      window.addEventListener('resize', () => { if (!root.hidden) { this.position(); this.select(-1); } }, { signal });
    }
    get toggles() { return [...document.querySelectorAll('[data-tl-search-toggle]')]; }
    position() {
      const bottom = document.querySelector('.tl-header')?.getBoundingClientRect().bottom || 0;
      this.root.style.setProperty('--tl-search-top', Math.max(0, bottom) + 'px');
      this.defaults.querySelectorAll('img').forEach(image => { image.loading = matchMedia('(min-width:750px)').matches ? 'eager' : 'lazy'; });
    }
    open(toggle) {
      document.dispatchEvent(new CustomEvent('tl:header:close-drawer'));
      this.opener = toggle;
      document.querySelectorAll('.tl-header details[open]').forEach(d => { d.open = false; });
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.position();
      this.root.hidden = false;
      this.toggles.forEach(t => t.setAttribute('aria-expanded', 'true'));
      this.input.setAttribute('aria-expanded', 'true');
      this.input.focus({ preventScroll: true });
    }
    close(returnFocus) {
      if (this.root.hidden) return;
      this.clear();
      this.input.value = '';
      this.root.hidden = true;
      document.body.style.overflow = this.previousOverflow;
      this.toggles.forEach(t => t.setAttribute('aria-expanded', 'false'));
      this.input.setAttribute('aria-expanded', 'false');
      if (returnFocus) (this.opener?.isConnected ? this.opener : this.toggles[0])?.focus({ preventScroll: true });
    }
    clear() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.sequence++;
      this.results.hidden = true;
      this.results.replaceChildren();
      this.defaults.hidden = false;
      this.error.hidden = true;
      this.reset.hidden = true;
      this.input.removeAttribute('aria-busy');
      this.status.textContent = '';
      this.select(-1);
    }
    options() {
      if (!this.input.value.trim()) return [...this.defaults.querySelectorAll('[data-search-option]')];
      if (this.results.hidden) return [];
      const order = matchMedia('(min-width:750px)').matches ? ['articles', 'products'] : ['products', 'articles'];
      return order.flatMap(group => [...this.results.querySelectorAll(`[data-search-group="${group}"] [data-search-option]`)]);
    }
    select(index) {
      const options = this.options();
      this.selected = index;
      this.root.querySelectorAll('[data-search-option]').forEach(option => option.setAttribute('aria-selected', 'false'));
      options.forEach((option, i) => {
        option.id = this.root.id + (this.input.value.trim() ? '-Result-' : '-Popular-') + i;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(i === index));
      });
      if (index < 0 || !options[index]) this.input.removeAttribute('aria-activedescendant');
      else {
        this.input.setAttribute('aria-activedescendant', options[index].id);
        options[index].scrollIntoView({ block: 'nearest' });
      }
    }
    keydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      } else if (event.target === this.input && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        const count = this.options().length;
        if (count) this.select(this.selected < 0 ? (event.key === 'ArrowDown' ? 0 : count - 1) : (this.selected + (event.key === 'ArrowDown' ? 1 : -1) + count) % count);
      } else if (event.target === this.input && event.key === 'Enter' && this.selected >= 0) {
        const option = this.options()[this.selected];
        if (option) { event.preventDefault(); option.click(); }
      } else if (event.key === 'Tab') {
        const focusable = [...this.shell.querySelectorAll('a[href],button,input')].filter(e => e.checkVisibility({ checkVisibilityCSS: true }) && !e.disabled && e.tabIndex >= 0);
        const first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    schedule(immediate = false) {
      clearTimeout(this.timer);
      this.request?.abort();
      const term = this.input.value.trim();
      const sequence = ++this.sequence;
      this.select(-1);
      this.reset.hidden = !term;
      this.error.hidden = true;
      const fallback = new URL(this.form.action, location.href);
      fallback.searchParams.set('q', term);
      this.root.querySelector('[data-search-fallback]').href = fallback.href;
      if (!term) { this.clear(); return; }
      this.results.hidden = true;
      this.defaults.hidden = true;
      this.input.setAttribute('aria-busy', 'true');
      this.status.textContent = this.root.querySelector('[data-search-loading-text]').textContent;
      this.timer = setTimeout(() => this.load(term, sequence), immediate ? 0 : 300);
    }
    async load(term, sequence) {
      if (this.cache.has(term)) { this.show(this.cache.get(term), sequence); return; }
      this.request = new AbortController();
      this.input.setAttribute('aria-busy', 'true');
      this.status.textContent = this.root.querySelector('[data-search-loading-text]').textContent;
      const url = new URL(this.root.dataset.endpoint, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product,article');
      url.searchParams.set('section_id', this.root.dataset.sectionId);
      try {
        const response = await fetch(url, { signal: this.request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-search-render]');
        if (!parsed) throw new Error('Search response unavailable');
        const markup = parsed.outerHTML;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, markup);
        this.show(markup, sequence);
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence || this.root.hidden) return;
        this.defaults.hidden = true;
        this.results.hidden = true;
        this.error.hidden = false;
        this.status.textContent = this.error.textContent.trim();
        this.input.removeAttribute('aria-busy');
      }
    }
    show(markup, sequence) {
      if (sequence !== this.sequence || this.root.hidden) return;
      this.results.innerHTML = markup;
      this.results.hidden = false;
      this.defaults.hidden = true;
      this.error.hidden = true;
      this.input.removeAttribute('aria-busy');
      this.status.textContent = this.root.querySelector('[data-search-count-text]').textContent.replace('__COUNT__', this.options().length);
      this.select(-1);
    }
    destroy() {
      this.close(false);
      clearTimeout(this.timer);
      this.request?.abort();
      this.events.abort();
    }
  }
  function initialize(root = document) {
    root.querySelectorAll('[data-tl-search-toggle]').forEach(toggle => toggle.setAttribute('role', 'button'));
    document.querySelectorAll('[data-tl-header-search]').forEach(node => { if (!node.hidden) instances.get(node)?.position(); });
    root.querySelectorAll('[data-tl-header-search]').forEach(node => {
      if (!instances.has(node)) instances.set(node, new HeaderSearch(node));
    });
  }
  window[registry] = initialize;
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-tl-header-search]').forEach(node => { instances.get(node)?.destroy(); instances.delete(node); }));
})();
